import { PHOTO_MAX_BYTES, PHOTO_SIZE_PX } from '@zip2live/shared';

/**
 * Profile photos are prepared in the browser: the centre square of the picture, resized to 512 px
 * and encoded as WebP (JPEG where the browser can't encode WebP, e.g. older Safari). That keeps
 * uploads small and needs no image service. The API still checks size, type and content.
 */
export async function squarePhoto(file: File): Promise<Blob> {
  const image = await decode(file);
  const { width, height } = image;
  if (!width || !height) throw new Error('That file isn’t a picture we can read.');
  const side = Math.min(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = PHOTO_SIZE_PX;
  const ctx = canvas.getContext('2d')!;
  // Transparent pictures (logos) get a white ground instead of black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, PHOTO_SIZE_PX, PHOTO_SIZE_PX);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, (width - side) / 2, (height - side) / 2, side, side, 0, 0, PHOTO_SIZE_PX, PHOTO_SIZE_PX);
  if ('close' in image) image.close();
  for (const quality of [0.86, 0.7, 0.5]) {
    const webp = await toBlob(canvas, 'image/webp', quality);
    // Browsers that can't encode WebP hand back a PNG instead.
    const blob = webp?.type === 'image/webp' ? webp : await toBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= PHOTO_MAX_BYTES) return blob;
  }
  throw new Error('That picture is too big. Try another one.');
}

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  try {
    // Respects the camera's rotation (EXIF), so phone photos aren't sideways.
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Some formats (e.g. HEIC on Safari) decode in an <img> but not in createImageBitmap.
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } catch {
      throw new Error('That file isn’t a picture we can read.');
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
